import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IFormbuilderFieldTypeConfiguration,
  ITableWithInputColumnFormModel,
  ITableWithInputConfigurationFormModel,
} from '../../../models/form-builder-types-configurations.model';
import { FormBuilderService } from '../../../services/form-builder.service';
import { IFormFieldsValidationModel } from '../../../models/form-fields-formly-validation.model';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import {
  EIremboFormlyFieldTypes,
  TableWithInputColumn,
} from '@irembo-andela/irembogov3-common';
import { getDefaultBaseSettingsConfigFormly } from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import { setRequiredValidatorUpdateFieldFromModel } from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { parseRequiredValidatorParseFieldIntoModel } from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';

export const defaultTableWithInputConfigFormModel: ITableWithInputConfigurationFormModel =
  {
    fieldLabel: null,
    placeholder: null,
    hint: null,
    tooltip: null,
    internalDescription: null,
    columns: [],
    minItems: 1,
    maxItems: null,
    enableEdit: true,
    enableDeletion: true,
    enableRowSelection: false,
    showSearch: false,
    showPagination: true,
    itemsPerPage: 10,
  };

export const tableWithInputFieldGetConfigurationConfig = (
  formBuilderService: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: defaultTableWithInputConfigFormModel,
      formlyForm: [
        ...getDefaultBaseSettingsConfigFormly(formBuilderService),
        {
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Table Configuration',
          },
          fieldGroup: [
            {
              key: 'columns',
              type: EIremboFormlyFieldTypes.customexpansionrepeater,
              props: {
                label: 'Field',
                addText: 'Add Field',
                hideRepeaterLabel: true,
                required: false,
                titleFieldName: 'label',
              },
              fieldArray: {
                fieldGroup: [
                  {
                    key: 'label',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Column Label',
                      required: true,
                    },
                  },
                  {
                    key: 'key',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Column Key',
                      required: true,
                      description: 'Unique key this value is stored under',
                    },
                  },
                  {
                    key: 'type',
                    type: EIremboFormlyFieldTypes.customdropdown,
                    defaultValue: 'input',
                    props: {
                      label: 'Field Type',
                      required: true,
                      bindLabel: 'label',
                      bindValue: 'value',
                      options: [
                        { label: 'Text', value: 'input' },
                        { label: 'Number', value: 'number' },
                        { label: 'Date', value: 'customdatepicker' },
                        { label: 'Dropdown Selector', value: 'customdropdown' },
                        { label: 'Checkbox', value: 'checkbox' },
                      ],
                    },
                  },
                  {
                    key: 'required',
                    type: EIremboFormlyFieldTypes.checkbox,
                    defaultValue: false,
                    props: {
                      label: 'Required',
                      formCheck: 'switch',
                    },
                  },
                  {
                    key: 'errorMessage',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Error Message',
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) =>
                        !field.parent?.model?.required,
                    },
                  },
                  {
                    key: 'unique',
                    type: EIremboFormlyFieldTypes.checkbox,
                    defaultValue: false,
                    props: {
                      label: 'Must Be Unique Across Rows',
                      formCheck: 'switch',
                    },
                  },
                  {
                    key: 'uniquenessErrorMessage',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Uniqueness Error Message',
                      description: 'e.g., This value already exists',
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) =>
                        !field.parent?.model?.unique,
                    },
                  },
                  {
                    key: 'hiddenColumn',
                    type: EIremboFormlyFieldTypes.checkbox,
                    defaultValue: false,
                    props: {
                      label: 'Hide Column In Table',
                      formCheck: 'switch',
                    },
                  },
                  {
                    key: 'widthPercent',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      type: 'number',
                      label: 'Column Width (%)',
                      min: 10,
                      max: 100,
                    },
                  },
                  {
                    key: 'multiple',
                    type: EIremboFormlyFieldTypes.checkbox,
                    defaultValue: false,
                    props: {
                      label: 'Allow Multiple Selection',
                      formCheck: 'switch',
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) =>
                        field.parent?.model?.type !== 'customdropdown',
                    },
                  },
                  {
                    key: 'options',
                    type: EIremboFormlyFieldTypes.customexpansionrepeater,
                    wrappers: ['custom-expansion-panel-with-icon'],
                    props: {
                      label: 'Options',
                      addText: 'Add Option',
                      hideRepeaterLabel: true,
                      required: false,
                      titleFieldName: 'label',
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) =>
                        field.parent?.model?.type !== 'customdropdown',
                    },
                    fieldArray: {
                      fieldGroup: [
                        {
                          key: 'label',
                          type: EIremboFormlyFieldTypes.input,
                          props: {
                            label: 'Option Label',
                            required: true,
                          },
                        },
                        {
                          key: 'value',
                          type: EIremboFormlyFieldTypes.input,
                          props: {
                            label: 'Option Value',
                            required: true,
                          },
                        },
                      ],
                    },
                  },
                ],
              },
            },
          ],
        },
        {
          key: 'minItems',
          type: EIremboFormlyFieldTypes.input,
          defaultValue: 1,
          props: {
            type: 'number',
            label: 'Minimum Number Of Items',
            min: 0,
            disabled: true,
          },
        },
        {
          key: 'maxItems',
          type: EIremboFormlyFieldTypes.input,
          props: {
            type: 'number',
            label: 'Maximum Number Of Items',
            min: 1,
          },
        },
        {
          key: 'enableEdit',
          type: EIremboFormlyFieldTypes.checkbox,
          props: {
            label: 'Allow Editing Rows',
            formCheck: 'switch',
          },
        },
        {
          key: 'enableDeletion',
          type: EIremboFormlyFieldTypes.checkbox,
          props: {
            label: 'Allow Deleting Rows',
            formCheck: 'switch',
          },
        },
        {
          key: 'enableRowSelection',
          type: EIremboFormlyFieldTypes.checkbox,
          props: {
            label: 'Enable Row Selection',
            formCheck: 'switch',
          },
        },
        {
          key: 'showSearch',
          type: EIremboFormlyFieldTypes.checkbox,
          props: {
            label: 'Show Search',
            formCheck: 'switch',
          },
        },
        {
          key: 'showPagination',
          type: EIremboFormlyFieldTypes.checkbox,
          props: {
            label: 'Show Pagination',
            formCheck: 'switch',
          },
        },
        {
          key: 'itemsPerPage',
          type: EIremboFormlyFieldTypes.input,
          props: {
            type: 'number',
            label: 'Items Per Page',
            min: 1,
          },
          expressions: {
            hide: '!model.showPagination',
          },
        },
      ],
    },
    validations: {
      formModel: {
        required: false,
      },
      formlyForm: [
        {
          key: 'validation',
          wrappers: ['panel-wrapper'],
          props: {
            label: 'Validation',
          },
          fieldGroup: [
            {
              key: 'required',
              type: EIremboFormlyFieldTypes.checkbox,
              props: {
                label: 'Required',
                formCheck: 'switch',
              },
            },
          ],
        },
      ],
    },
    display: {
      formModel: {},
      formlyForm: [],
    },
  };
};

// The settings form edits each column as a flat object (label, key, type,
// required, unique, hiddenColumn, widthPercent, options, multiple), but the
// runtime table-with-input field (custom-table-with-input) only ever reads
// column settings nested under `column.props` (TableWithInputColumn). These
// two helpers keep that shape mismatch confined to this file: one nests the
// flat form values on save, the other flattens them back out for editing.
const mapColumnFormModelToTableWithInputColumn = (
  column: ITableWithInputColumnFormModel
): TableWithInputColumn => {
  // 'number' isn't a real formly type — the runtime resolves it from
  // type: 'input' + props.type: 'number' (see
  // resolveTableWithInputColumnKind in irembo-table-with-input.model.ts).
  const isNumber = column.type === 'number';
  return {
    key: column.key,
    type: column.type === 'number' ? 'input' : column.type,
    props: {
      label: column.label,
      ...(isNumber && { type: 'number' }),
      required: column.required ?? false,
      unique: column.unique ?? false,
      hiddenColumn: column.hiddenColumn ?? false,
      widthPercent: column.widthPercent ?? null,
      multiple: column.multiple ?? false,
      options: column.options ?? [],
    },
    ...((column.errorMessage || column.uniquenessErrorMessage) && {
      validation: {
        messages: {
          ...(column.errorMessage && { required: column.errorMessage }),
          ...(column.uniquenessErrorMessage && {
            unique: column.uniquenessErrorMessage,
          }),
        },
      },
    }),
  };
};

const mapTableWithInputColumnToColumnFormModel = (
  // Legacy columns saved before this fix stored these fields flat instead of
  // nested under `props` — fall back to the flat reads so those don't appear
  // blank when reopened.
  column: Partial<TableWithInputColumn> &
    Partial<ITableWithInputColumnFormModel>
): ITableWithInputColumnFormModel => ({
  label: column.props?.label ?? column.label ?? '',
  key: column.key ?? '',
  type:
    column.type === 'input' && column.props?.type === 'number'
      ? 'number'
      : (column.type as ITableWithInputColumnFormModel['type'] | undefined) ??
        'input',
  required: column.props?.required ?? column.required ?? false,
  errorMessage: column.validation?.messages?.required ?? '',
  unique: column.props?.unique ?? column.unique ?? false,
  uniquenessErrorMessage: column.validation?.messages?.unique ?? '',
  hiddenColumn: column.props?.hiddenColumn ?? column.hiddenColumn ?? false,
  widthPercent: column.props?.widthPercent ?? column.widthPercent ?? null,
  multiple: column.props?.multiple ?? column.multiple ?? false,
  options:
    (column.props?.options as { label: string; value: string }[] | undefined) ??
    column.options ??
    [],
});

export const tableWithInputFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): ITableWithInputConfigurationFormModel => {
  return {
    ...defaultTableWithInputConfigFormModel,
    fieldKey: (field.key as string) ?? '',
    fieldLabel: field.props?.label ?? '',
    placeholder: field.props?.placeholder ?? '',
    hint: field.props?.['hint'] ?? '',
    tooltip: field.props?.['tooltip'] || '',
    internalDescription: field.props?.['internalDescription'] ?? '',
    columns: (field.props?.['columns'] || []).map(
      mapTableWithInputColumnToColumnFormModel
    ),
    minItems: field.props?.['minItems'] ?? 1,
    maxItems: field.props?.['maxItems'] ?? null,
    enableEdit: field.props?.['enableEdit'] ?? true,
    enableDeletion: field.props?.['enableDeletion'] ?? true,
    enableRowSelection: field.props?.['enableRowSelection'] ?? false,
    showSearch: field.props?.['showSearch'] ?? false,
    showPagination: field.props?.['showPagination'] ?? true,
    itemsPerPage: field.props?.['itemsPerPage'] ?? 10,
  };
};

export const tableWithInputFieldUpdateFromSettingsFormModel = (
  model: ITableWithInputConfigurationFormModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  field.props = field.props || {};

  field.props['label'] = model.fieldLabel || undefined;
  field.props['placeholder'] = model.placeholder || undefined;
  field.props['hint'] = model.hint;
  field.props['tooltip'] = model.tooltip;
  field.props['internalDescription'] = model.internalDescription;

  field.props['columns'] = model.columns.map(
    mapColumnFormModelToTableWithInputColumn
  );
  field.props['minItems'] = model.minItems ?? undefined;
  field.props['maxItems'] = model.maxItems ?? undefined;
  field.props['enableEdit'] = model.enableEdit;
  field.props['enableDeletion'] = model.enableDeletion;
  field.props['enableRowSelection'] = model.enableRowSelection;
  field.props['showSearch'] = model.showSearch;
  field.props['showPagination'] = model.showPagination;
  field.props['itemsPerPage'] = model.itemsPerPage;

  return field;
};

export const tableWithInputFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  return parseRequiredValidatorParseFieldIntoModel({}, field);
};

export const tableWithInputFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setRequiredValidatorUpdateFieldFromModel(model, field);
};

export const tableWithInputFieldParseIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const tableWithInputFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};
